import {Channel, channel} from 'redux-saga';
import {call, cancel, fork, put, select, takeEvery} from 'redux-saga/effects';
import {AppState} from '../../../../../../state/store';
import {TextViewerLoader} from '../class/TextViewerLoader.class';
import {events} from '../constant/textViewerSagaEvents';
import {TextViewerViewData} from '../state/TextViewerViewData.interface';
import {ChapterLoadData} from '../type/ChapterLoadData.interface';
import {ContentPreloadData} from '../type/ContentPreloadData.interface';
import {MetaLoadData} from '../type/MetaLoadData.interface';

export function* loadTextViewerData({
  seriesId,
  productId,
}: {
  seriesId: number;
  productId: number;
}) {
  const viewData: TextViewerViewData | undefined = yield select(
    (state: AppState) => state.textViewer.viewData[productId]
  );
  if (!viewData) {
    return;
  }

  const {urls, currentChapterId, currentPage} = viewData;
  if (!urls || currentChapterId === undefined || currentPage === undefined) {
    return;
  }

  const {metaUrl, contentList} = urls;

  const textViewerLoader = new TextViewerLoader({
    seriesId: String(seriesId),
    productId: String(productId),
    metaUrl,
    contentUrlInfoList: contentList,
  });

  textViewerLoader.ready({
    preloadChapterIds: [
      currentChapterId,
      currentChapterId - 1,
      currentChapterId + 1,
    ],
  });

  const metaLoadChannel: Channel<MetaLoadData> = yield call(channel);
  const contentPreloadChannel: Channel<ContentPreloadData> =
    yield call(channel);
  const chapterLoadedChannel: Channel<ChapterLoadData> = yield call(channel);

  textViewerLoader.onMetaLoaded = loader => {
    metaLoadChannel.put({
      metaInfo: loader.metaInfo,
      loadedInfo: loader.loadedInfo,
    });
  };

  textViewerLoader.onContentPreloaded = (loader, chapterId) => {
    const htmlInfo = loader.getChapter(chapterId);
    contentPreloadChannel.put({productId, chapterId, htmlInfo});
  };

  textViewerLoader.onChapterLoaded = (loader, chapterId) => {
    const htmlInfo = loader.getChapter(chapterId);
    chapterLoadedChannel.put({productId, chapterId, htmlInfo});
  };

  // @ts-expect-error
  const metaLoadTask = yield fork(function* () {
    yield takeEvery(metaLoadChannel, function* (result: MetaLoadData) {
      yield put(
        events.textViewerMetaLoadCompleted({
          productId,
          metaInfo: result.metaInfo,
          loadedInfo: result.loadedInfo,
        })
      );
    });
  });

  // @ts-expect-error
  const preloadTask = yield fork(function* () {
    yield takeEvery(
      contentPreloadChannel,
      function* (result: ContentPreloadData) {
        yield put(events.textViewerPreloadCompleted(result));
      }
    );
  });

  // @ts-expect-error
  const chapterLoadTask = yield fork(function* () {
    yield takeEvery(chapterLoadedChannel, function* (result: ChapterLoadData) {
      yield put(events.textViewerChapterLoadCompleted(result));
    });
  });

  yield call(textViewerLoader.load);

  yield cancel(metaLoadTask);
  yield cancel(preloadTask);
  yield cancel(chapterLoadTask);
}
